import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { ScrollBlock } from '@/components/ScrollBlock';
import { getSelectOptionsHeight } from '@/helpers/primitives';
import { useComponentVisible } from '@/hooks/visibility';
import { ButtonClose } from '../../buttons/ButtonClose';
import { FieldStructure } from '../structures/FieldStructure';
import {
  ScOptions,
  type ScOptionsProps,
  ScSelectBlock,
  ScSelectSearch,
} from './styled';
import type { SelectBlockProps, SelectOption } from './types';

export const SelectBlock = forwardRef(
  (
    {
      placeholder,
      options,
      selected,
      error,
      hideErrorMessage,
      onSelectOption,
      disabled,
      defaultValue,
      dropdownUp,
      search,
      withoutIcon,
      secondary,
      isFilter,
      canReset,
      addons,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);

    const handleOpen = useCallback(() => {
      if (!disabled) {
        setIsComponentVisible(!isComponentVisible);
      }
    }, [disabled, isComponentVisible, setIsComponentVisible]);

    const selectedOption = useMemo(
      () => options.find(({ value }) => value === selected),
      [selected, options]
    );

    const handleReset = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onSelectOption && onSelectOption('');
      },
      [onSelectOption]
    );

    return (
      <FieldStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScSelectBlock
          active={isComponentVisible}
          className={isComponentVisible ? 'active' : ''}
          disabled={!!disabled}
          error={!!error}
          isFilter={isFilter}
          ref={forwardedRef}
          secondary={secondary}
          selected={!!selectedOption}
        >
          <div className="select-button" onClick={handleOpen} ref={openerRef}>
            <div
              className={`value ${!withoutIcon && !!selectedOption?.icon && 'with-icon'}`}
            >
              {!withoutIcon && !!selectedOption?.icon && (
                <ImageComponent
                  alt="option"
                  height={24}
                  key={selectedOption.value}
                  src={selectedOption.icon}
                  width={24}
                />
              )}
              <div className="name">
                {selectedOption
                  ? (selectedOption.nameV ?? selectedOption.name)
                  : defaultValue}
                {!(selectedOption || defaultValue) && !!placeholder && (
                  <span className="placeholder">{placeholder}</span>
                )}
              </div>
            </div>
            <div className={`${selected && canReset ? 'cross' : 'arrow'}`}>
              {selected && canReset ? (
                <ButtonClose onClick={handleReset} />
              ) : (
                <IconComponent name="whiteArrowRight" size="20px" />
              )}
            </div>
          </div>
          {isComponentVisible && (
            <OptionsBlock
              addons={addons}
              dropdownUp={dropdownUp}
              onChange={(value) => {
                onSelectOption && onSelectOption(value);
                handleOpen();
              }}
              options={options}
              ref={ref}
              search={search}
              selectedOption={selectedOption}
              {...scProps}
            />
          )}
        </ScSelectBlock>
      </FieldStructure>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  addons?: JSX.Element | null;
  scrollBlockTopMargin?: number;
}

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      search,
      addons,
      scrollBlockTopMargin,
      ...props
    },
    forwardedRef
  ) => {
    const [searchValue, setSearchValue] = useState('');

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (!(ref.current && search)) return;

      ref.current.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (value: string) => {
        setSearchValue(value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || !search) return options;

      return options.filter(({ name }) => {
        if (typeof name !== 'string') return;
        return name.toLowerCase().includes(searchValue.toLowerCase());
      });
    }, [searchValue, options, search]);

    const height = getSelectOptionsHeight(filteredOptions.length, !!search);

    return (
      <ScOptions {...props} ref={forwardedRef} search={search}>
        <div className="content">
          {search && (
            <ScSelectSearch>
              <input
                onChange={({ target }) => handleChangeSearchValue(target.value)}
                placeholder="Search..."
                ref={ref}
                type="text"
              />
              <div className="divider" />
            </ScSelectSearch>
          )}
          {filteredOptions.length ? (
            <ScrollBlock height={height} scrollTopMargin={scrollBlockTopMargin}>
              <ul>
                {filteredOptions.map(({ icon, name, value }, index) => (
                  <li
                    className={`${selectedOption?.value === value && 'active'} ${!!icon && 'with-icon'}`}
                    key={index}
                    onClick={() => onChange(value)}
                  >
                    <div className="content">
                      {icon && (
                        <ImageComponent
                          alt={typeof name === 'string' ? name : 'icon'}
                          height={24}
                          key={value}
                          src={icon}
                          width={24}
                        />
                      )}
                      {name}
                    </div>
                  </li>
                ))}
              </ul>
            </ScrollBlock>
          ) : (
            <ul>
              <li className="not-found">Not found</li>
            </ul>
          )}
          {addons && <div className="addons">{addons}</div>}
        </div>
      </ScOptions>
    );
  }
);
